import { FC, useCallback, useEffect, useRef, useState } from 'react'
import { ClipboardList, UserCheck } from 'lucide-react';
import { Col, Row } from 'react-bootstrap';
import { EvaluationStatusFilter, IProjectEvaluationListData, IProjectEvaluator } from '../../../project/core/_models';
import { getEvaluationProjectsList } from '../../../project/core/_project_evaluation';
import { useSnackbar } from '../../../../../_theme/context/SnackbarContext';
import SimpleLoader from '../../../apps/components/SimpleLoader';
import ProjectEvaluationModal from './ProjectEvaluationModal';
import ProjectCard from './ProjectCard';
import ProjectReminderLogModal from './ProjectReminderLogModal';
import { LogError } from '../../../../../utils/GlobalLogging';
import SkeletonCard from '../../../apps/components/skeleton/SkeletonCard';
import { IDashboardFilterData } from '../../../core/_models';
import { getApiErrorMessage } from "../../../../../utils/utils";
import { isAbortedError } from "../../../../../utils/global";

interface ProjectListProps {
    search: string;
    activeTab: EvaluationStatusFilter;
    handleEvaluatorMode: (mode: boolean) => void;
    evaluatorModeOn: boolean;
    filters: IDashboardFilterData;
}
const ProjectList: FC<ProjectListProps> = ({ search, activeTab, handleEvaluatorMode, evaluatorModeOn, filters }) => {
    const { showSnackbar } = useSnackbar();
    const [loading, setLoading] = useState<boolean>(true);
    const [listing, setListing] = useState<IProjectEvaluationListData[]>([]);
    const [page, setPage] = useState<number>(1);
    const [totalPages, setTotalPages] = useState<number>(0);
    const [showProjectEvaluationModal, setShowProjectEvaluationModal] = useState<boolean>(false);
    const [selectedProject, setSelectedProject] = useState<IProjectEvaluationListData | null>(null);
    const [showProjectReminderLogModal, setShowProjectReminderLogModal] = useState<boolean>(false);
    const abortControllerRef = useRef<AbortController | null>(null);

    const fetchProjects = useCallback(async () => {
        abortControllerRef.current?.abort();
        const controller = new AbortController();
        abortControllerRef.current = controller;

        try {
            setLoading(true);
            const response = await getEvaluationProjectsList({
                filters,
                page,
                search,
                eval_status_filter: activeTab,
                signal: controller.signal,
            });
            if (controller.signal.aborted) return;
            if (response.status === 'ok') {
                setListing(prev =>
                    page === 1
                        ? response.data.projects
                        : [...prev, ...response.data.projects]
                );
                setTotalPages(response.data.total_pages);
            } else {
                showSnackbar(response.message, "danger");
            }
        } catch (error) {
            if (isAbortedError(error)) return;
            LogError("Failed to fetch projects:", error,"ProjectList");
            showSnackbar(getApiErrorMessage(error, "Failed to fetch projects"), "danger");
        }
        finally {
            if (!controller.signal.aborted) {
                setLoading(false);
            }
        }
    }, [showSnackbar, page, search, activeTab, filters]);



    useEffect(() => {
        setPage(1);
        setListing([]);
    }, [search, activeTab, filters]);

    useEffect(() => {
        fetchProjects();
        return () => {
            abortControllerRef.current?.abort();
        };
    }, [fetchProjects]);


    const handleLoadMore = useCallback(() => {
        if (!loading && page < totalPages) {
            setPage(prev => prev + 1);
        }
    }, [loading, page, totalPages]);

    const handleCloseProjectEvaluationModal = useCallback(() => {
        setShowProjectEvaluationModal(false);
        setSelectedProject(null);
    }, []);
    const handleOpenProjectEvaluationModal = useCallback((project: IProjectEvaluationListData) => {
        setSelectedProject(project);
        setShowProjectEvaluationModal(true);
    }, []);
    const updateProjectEvaluation = useCallback((projectId: number, evaluators: IProjectEvaluator[]) => {
        setListing(prev =>
            prev.map(p =>
                p.project_id === projectId ? { ...p, evaluators } : p
            )
        );
    }, []);
    const handleOpenProjectReminderLogModal = useCallback((project: IProjectEvaluationListData) => {
        setSelectedProject(project);
        setShowProjectReminderLogModal(true);
    }, []);
    const handleCloseProjectReminderLogModal = useCallback(() => {
        setShowProjectReminderLogModal(false);
        setSelectedProject(null);
    }, []);
    return (
        <div className="gestalt-group group border-0 shadow-sm rounded-5 p-5 w-100 mb-4 mb-lg-0">
            <div className="d-flex justify-content-between align-items-center">
                <h2 className={`apple-title fw-bold mb-0 fs-4`}><ClipboardList className="me-2 text-info" />Projects</h2>
                {(activeTab === EvaluationStatusFilter.pending || activeTab === EvaluationStatusFilter.overdue) && <>
                    <div className="d-flex align-items-center flex-column flex-sm-row justify-content-between gap-4 w-100 w-lg-auto">
                        <button
                            onClick={() => handleEvaluatorMode(false)}
                            className={`p-3 border border-0 rounded-4 ${!evaluatorModeOn
                                ? "bg-primary text-white shadow-apple-sm"
                                : "text-muted bg-white"
                                }`}
                        >
                            <ClipboardList className="h-4 w-4" /> Project View
                        </button>
                        <button
                            onClick={() => handleEvaluatorMode(true)}
                            className={`p-3 border border-0 rounded-4 ${evaluatorModeOn
                                ? "bg-primary text-white shadow-apple-sm"
                                : "text-muted bg-white"
                                }`}
                        >
                            <UserCheck className="h-4 w-4" /> Evaluator View
                        </button>
                    </div>
                </>}
            </div>

            <Row className="mt-5 g-4">
                {loading && listing.length === 0 ? (
                    Array.from({ length: 15 }).map((_, index) => (
                        <div key={index} className="col-12 col-sm-6">
                            <SkeletonCard
                                wrapperClassName=""
                                className="mb-4"
                                titleWidth="75%"
                                valueWidth="75%"
                                showIcon={false}
                                lines={1}
                            />
                        </div>
                    ))
                ) : (listing.map((project) => (
                    <Col key={project.project_id} className="col-12 col-md-6 ">
                        <ProjectCard key={project.project_id} project={project} onReminderClick={handleOpenProjectEvaluationModal} onReminderLogClick={handleOpenProjectReminderLogModal} />
                    </Col>
                )))}
            </Row>
            {!loading && listing.length === 0 && (
                <div className="text-center text-muted py-5">
                    No projects found.
                </div>
            )}
            {(page < totalPages) && <div className="d-flex justify-content-center mt-5">
                <button className="btn btn-primary btn-sm rounded-pill py-2" disabled={loading} onClick={handleLoadMore}>Load More {loading && <span className="spinner-border spinner-border-sm ms-2" role="status" aria-hidden="true"></span>}</button>
            </div>}
            {selectedProject && showProjectEvaluationModal && <ProjectEvaluationModal projectEvaluation={selectedProject} show={showProjectEvaluationModal} onClose={handleCloseProjectEvaluationModal} updateProjectEvaluation={updateProjectEvaluation} />}
            {selectedProject && showProjectReminderLogModal && <ProjectReminderLogModal projectEvaluation={selectedProject} show={showProjectReminderLogModal} onClose={handleCloseProjectReminderLogModal} />}
        </div>
    )
}
export default ProjectList;